import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get } from 'lodash';
import { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { KnowledgeBaseApi } from '@/Domain/api/KnowledgeBase/KnowledgeBase.api';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import {
  ImportSinglePageDto,
  ImportMultiplePagesDto,
  ImportSitemapDto,
  ImportAllPagesDto,
} from '@/Domain/api/KnowledgeBase/dto/ImportUrl.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { AppHelper } from '@/Domain/helper/App.helper';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useArticleLimitCheck } from '@/Domain/hooks/KnowledgeBase/useArticleLimitCheck.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { useUploadStore } from '@/Domain/store/upload.store';
import { extractUrlHost } from '@/UI/GettingStarted/components/useAutomaticOnboarding';
import {
  setSiteMapPending,
  SITEMAP_CONSUMED_KEY,
} from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

const uploadUrlSchema = z
  .object({
    url: z
      .string()
      .min(1, i18n.t('knowledge_base:modals.upload_url.errors.url_required'))
      .refine((url) => {
        if (/[\s,;]/.test(url)) return true;

        try {
          new URL(AppHelper.addProtocol(url));
          return true;
        } catch {
          return false;
        }
      }, i18n.t('knowledge_base:modals.upload_url.errors.invalid_url')),
    htmlSelector: z.string().optional().default(''),
    filterPhrase: z.string().optional().default(''),
    useHtmlSelector: z.boolean().default(false),
    useFilterPhrase: z.boolean().default(false),
    tags: z.array(z.string()).optional().default([]),
    summarize: z.boolean().optional().default(false),
  })
  .refine(
    (data) => {
      if (data.useHtmlSelector && (!data.htmlSelector || data.htmlSelector.trim() === '')) {
        return false;
      }
      return true;
    },
    {
      message: i18n.t('knowledge_base:modals.upload_url.html_selector.error_required'),
      path: ['htmlSelector'],
    },
  )
  .refine(
    (data) => {
      if (data.useFilterPhrase && (!data.filterPhrase || data.filterPhrase.trim() === '')) {
        return false;
      }
      return true;
    },
    {
      path: ['filterPhrase'],
    },
  );

export type UploadUrlForm = z.infer<typeof uploadUrlSchema>;

type ImportContext = {
  modalId: string;
  setError?: (error: string) => void;
  /**
   * When `true`, the import skips pushing items into `useUploadStore`, which
   * is what drives the bottom-right progress toast. Used by the manual
   * Getting Started flow — the onboarding card already gives the user
   * feedback that the page is being processed, so the detached toast on
   * top of it is redundant noise. Defaults to `false` (toast shows).
   */
  silent?: boolean;
};

type ImportUrlParams = {
  url: string;
  useHtmlSelector?: boolean;
  htmlSelector?: string;
  useFilterPhrase?: boolean;
  filterPhrase?: string;
  type: WebsiteModalTypeEnum;
  modalId: string;
  setError?: (error: string) => void;
  /** See `ImportContext.silent`. */
  silent?: boolean;
};

type ImportResult = {
  success: boolean;
  error: string | null;
  data?: ArticleDto | ArticleDto[];
};

type ProcessedImportData = {
  formattedUrl: string | string[];
  htmlSelector: string | null;
  phrases?: string[];
  linkFilterRegex?: string;
  type: WebsiteModalTypeEnum;
  modalId: string;
  setError?: (error: string) => void;
  silent?: boolean;
};

type ProcessedImportResult = {
  error: string | null;
  data?: ProcessedImportData;
};

export type ModalId =
  | typeof MODAL_NAMES.UPLOAD_URL_MODAL_ID
  | typeof MODAL_NAMES.UPLOAD_MULTIPLE_URLS_MODAL_ID
  | typeof MODAL_NAMES.UPLOAD_SITEMAP_MODAL_ID
  | typeof MODAL_NAMES.UPLOAD_ALL_PAGES_MODAL_ID
  | typeof MODAL_NAMES.UPLOAD_YOUTUBE_MODAL_ID;

interface ApiErrorResponse {
  errors?: {
    root?: { message: string; code: string }[];
  };
}

export const useImportUrl = () => {
  const { t } = useTranslation(['knowledge_base']);
  const queryClient = useQueryClient();
  const handleArticleLimitCheck = useArticleLimitCheck();
  const { capture } = usePostHogAnalytics();
  // `isOpen` is used to guard the post-success `toggleModal` call: we only
  // want to CLOSE a modal that was actually opened by a caller. Without the
  // guard, a caller that passes `modalId` but never opened the modal (e.g.
  // the Getting Started onboarding firing the same import endpoint as the
  // KB tab's modal) would accidentally OPEN that modal on success, since
  // `toggleModal` flips open↔closed based on current state.
  const { toggleModal, isOpen } = useModalStore();
  const { addItems } = useUploadStore();

  const addUrlsToUploadStore = (url: string | string[]) => {
    if (Array.isArray(url)) {
      url.forEach((u) => {
        addItems(u, UploadItemTypeEnum.URL);
      });
    } else {
      addItems(url, UploadItemTypeEnum.URL);
    }
  };

  const singlePageMutation = useMutation<
    ArticleDto,
    AxiosError<ApiErrorResponse>,
    ImportSinglePageDto & { context?: ImportContext }
  >({
    mutationKey: [Endpoint.knowledgeBase.website.singlePage],
    mutationFn: (data) => {
      const apiData: ImportSinglePageDto = {
        url: data.url,
        mode: data.mode,
        html_selector: data.html_selector,
      };
      return KnowledgeBaseApi.importSinglePage(apiData);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ArticleDto[]>(
        [Endpoint.knowledgeBase.website.singlePage],
        (oldData = []) => [data, ...oldData],
      );
      void queryClient.invalidateQueries({ queryKey: [Endpoint.knowledgeBase.website.singlePage] });
      // Match `useUploadArticles` — the KB tab's articles list reads from
      // `articlesInfinite`, so without this invalidation the newly scraped
      // article won't appear until a socket event (scrape completion) fires.
      // Matters most for the Getting Started onboarding, where the user may
      // navigate straight to the KB tab after hitting Continue.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });

      const isPartOfMultipleImport = !variables.context?.modalId;
      if (!isPartOfMultipleImport) {
        if (!variables.context?.silent) {
          addUrlsToUploadStore(variables.url);
        }

        if (variables.context?.modalId && isOpen(variables.context.modalId)) {
          toggleModal(variables.context.modalId);
        }
      }
    },
    onError: (error, variables) => {
      console.error('Error importing URL:', error);

      if (variables.context?.setError) {
        const errorMessage =
          get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed');
        variables.context.setError(errorMessage);
      }
    },
  });

  const multipageMutation = useMutation<
    ArticleDto,
    AxiosError<ApiErrorResponse>,
    ImportMultiplePagesDto & { context?: ImportContext }
  >({
    mutationKey: [Endpoint.knowledgeBase.website.multiplePages],
    mutationFn: (data) => {
      const apiData: ImportMultiplePagesDto = {
        links: data.links,
        html_selector: data.html_selector,
        tags: data.tags || [],
        summarize: data.summarize || false,
      };
      return KnowledgeBaseApi.importMultiplePages(apiData);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ArticleDto[]>(
        [Endpoint.knowledgeBase.website.multiplePages],
        (oldData = []) => [data, ...oldData],
      );
      void queryClient.invalidateQueries({
        queryKey: [Endpoint.knowledgeBase.website.multiplePages],
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });

      // One aggregate file per submit: the whole batch tracks as a single
      // "Scraping X of N pages" progress bar against the backend meta
      // article. Matches sitewide / sitemap under the unified protocol.
      if (variables.links && variables.links.length > 0) {
        addUrlsToUploadStore(UploadUrlModalHelper.getRepresentativeUrl(variables.links));
      }

      if (variables.context?.modalId) {
        toggleModal(variables.context.modalId);
      }
    },
    onError: (error, variables) => {
      console.error('Error importing multiple URLs:', error);

      if (variables.context?.setError) {
        const errorMessage =
          get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed');
        variables.context.setError(errorMessage);
      }
    },
  });

  const sitemapMutation = useMutation<
    ArticleDto,
    AxiosError<ApiErrorResponse>,
    ImportSitemapDto & { context?: ImportContext }
  >({
    mutationKey: [Endpoint.knowledgeBase.website.sitemap],
    mutationFn: (data) => {
      const apiData: ImportSitemapDto = {
        site_map: data.site_map,
        html_selector: data.html_selector,
        link_filter_regex: data.link_filter_regex || '.*',
        summarize: data.summarize || false,
      };
      return KnowledgeBaseApi.importSitemap(apiData);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ArticleDto[]>(
        [Endpoint.knowledgeBase.website.sitemap],
        (oldData = []) => [data, ...oldData],
      );
      void queryClient.invalidateQueries({ queryKey: [Endpoint.knowledgeBase.website.sitemap] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });

      // Sitemap now returns the meta article (same shape as /website), not
      // a prefetched link list. One file per submit, progress streams from
      // the Firecrawl webhook against the meta's id.
      if (variables.site_map) {
        addUrlsToUploadStore(variables.site_map);
      }

      if (variables.context?.modalId) {
        toggleModal(variables.context.modalId);
      }
    },
    onError: (error, variables) => {
      console.error('Error importing sitemap:', error);

      if (variables.context?.setError) {
        const errorMessage =
          get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed');
        variables.context.setError(errorMessage);
      }
    },
  });

  const allPagesMutation = useMutation<
    ArticleDto,
    AxiosError<ApiErrorResponse>,
    ImportAllPagesDto & { context?: ImportContext }
  >({
    mutationKey: [Endpoint.knowledgeBase.website.allPages],
    mutationFn: (data) => {
      const apiData: ImportAllPagesDto = {
        url: data.url,
        mode: data.mode,
        html_selector: data.html_selector,
        phrases: data.phrases,
      };
      return KnowledgeBaseApi.importAllPages(apiData);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ArticleDto[]>(
        [Endpoint.knowledgeBase.website.allPages],
        (oldData = []) => [data, ...oldData],
      );
      void queryClient.invalidateQueries({ queryKey: [Endpoint.knowledgeBase.website.allPages] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });

      // /website never returns a `links` array — the dead branch here was
      // inherited from a sitemap-style response shape that no longer exists.
      if (variables.url) {
        addUrlsToUploadStore(variables.url);
      }

      if (variables.context?.modalId) {
        toggleModal(variables.context.modalId);
      }
    },
    onError: (error, variables) => {
      console.error('Error importing all pages:', error);

      if (variables.context?.setError) {
        const errorMessage =
          get(error, 'response.data.errors.root[0].message') ||
          t('modals.upload_url.errors.import_failed');
        variables.context.setError(errorMessage);
      }
    },
  });

  const siteMapStartMutation = useMutation<unknown, AxiosError<ApiErrorResponse>, { url: string }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.start],
  });

  const validateUrl = (url: string, type: WebsiteModalTypeEnum): string | null => {
    if (!url.trim()) {
      return t('modals.upload_url.errors.url_required');
    }

    if (type === WebsiteModalTypeEnum.MULTIPLE) {
      const nonEmptyUrls = UploadUrlModalHelper.parseMultipleUrls(url);

      if (nonEmptyUrls.length === 0) {
        return t('modals.upload_url.errors.urls_required');
      }

      const allValid = nonEmptyUrls.every((u) =>
        UploadUrlModalHelper.validateUrl(AppHelper.addProtocol(u)),
      );
      if (!allValid) {
        return t('modals.upload_url.errors.contains_invalid_url');
      }

      return null;
    }

    if (!UploadUrlModalHelper.validateUrl(url)) {
      return t('modals.upload_url.errors.invalid_url');
    }

    return null;
  };

  const processImportParams = (params: ImportUrlParams): ProcessedImportResult => {
    const {
      url,
      type,
      useHtmlSelector = false,
      htmlSelector = '',
      useFilterPhrase = false,
      filterPhrase = '',
      modalId,
      setError,
      silent,
    } = params;

    const validationError = validateUrl(url, type);
    if (validationError) {
      return { error: validationError };
    }

    const processedHtmlSelector = useHtmlSelector ? htmlSelector : null;

    if (type === WebsiteModalTypeEnum.MULTIPLE) {
      const nonEmptyUrls = UploadUrlModalHelper.parseMultipleUrls(url).map(AppHelper.addProtocol);
      const uniqueUrls = Array.from(new Set(nonEmptyUrls));

      return {
        error: null,
        data: {
          formattedUrl: uniqueUrls,
          htmlSelector: processedHtmlSelector,
          type,
          modalId,
          setError,
          silent,
        },
      };
    } else {
      const phrases =
        useFilterPhrase && type === WebsiteModalTypeEnum.ALL_PAGES ? [filterPhrase] : undefined;

      const linkFilterRegex =
        useFilterPhrase && type === WebsiteModalTypeEnum.SITEMAP ? filterPhrase : undefined;

      return {
        error: null,
        data: {
          formattedUrl: url,
          htmlSelector: processedHtmlSelector,
          phrases,
          linkFilterRegex,
          type,
          modalId,
          setError,
          silent,
        },
      };
    }
  };

  const executeImport = async (
    data: ProcessedImportData,
  ): Promise<ArticleDto | ArticleDto[] | null> => {
    capture(PostHogEventEnum.ArticleUploadStarted, { upload_type: data.type });
    try {
      const context: ImportContext = {
        modalId: data.modalId,
        setError: data.setError,
        silent: data.silent,
      };

      // `handleArticleLimitCheck` returns `undefined` when the article limit is
      // hit (and opens the upgrade modal). We propagate that as `null` so the
      // caller can distinguish "limit reached" from "mutation succeeded" —
      // previously this returned `null` on success too because the async
      // callback's promise was discarded.
      const result = await handleArticleLimitCheck<Promise<ArticleDto | ArticleDto[] | null>>(
        async () => {
          switch (data.type) {
            case WebsiteModalTypeEnum.SINGLE:
            case WebsiteModalTypeEnum.YOUTUBE:
              return singlePageMutation.mutateAsync({
                url: data.formattedUrl as string,
                mode: 'individual',
                html_selector: data.htmlSelector,
                context,
              });

            case WebsiteModalTypeEnum.MULTIPLE:
              return multipageMutation.mutateAsync({
                links: data.formattedUrl as string[],
                html_selector: data.htmlSelector,
                tags: [],
                summarize: false,
                context,
              });

            case WebsiteModalTypeEnum.SITEMAP:
              return sitemapMutation.mutateAsync({
                site_map: data.formattedUrl as string,
                html_selector: data.htmlSelector,
                link_filter_regex: data.linkFilterRegex || '.*',
                summarize: false,
                context,
              });

            case WebsiteModalTypeEnum.ALL_PAGES:
              // Kick off the background map; the status dock opens the review modal when URLs are ready.
              try {
                await siteMapStartMutation.mutateAsync({ url: data.formattedUrl as string });
              } catch (error) {
                context.setError?.(
                  get(error, 'response.data.errors.root[0].message') ||
                    t('modals.upload_url.errors.import_failed'),
                );
                return null;
              }
              // A fresh map supersedes any previously-consumed one with the same shape.
              localStorage.removeItem(SITEMAP_CONSUMED_KEY);
              setSiteMapPending();
              capture(PostHogEventEnum.SiteScrapeMapStarted, {
                url_host: extractUrlHost(data.formattedUrl as string),
                source: data.silent ? 'onboarding_manual' : 'kb',
              });
              if (context?.modalId && isOpen(context.modalId)) {
                toggleModal(context.modalId);
              }
              return null;
          }
          return null;
        },
      );

      return result ?? null;
    } catch (error) {
      console.error(`Error importing ${data.type}:`, error);
      if (data.setError && error instanceof Error && !(error as AxiosError).response) {
        data.setError(t('modals.upload_url.errors.import_failed'));
      }
      return null;
    }
  };

  const importUrl = async (params: ImportUrlParams): Promise<ImportResult> => {
    const processed = processImportParams(params);
    if (processed.error) {
      if (params.setError) {
        params.setError(processed.error);
      }
      return { success: false, error: processed.error };
    }

    if (!processed.data) {
      if (params.setError) {
        params.setError(t('modals.upload_url.errors.invalid_parameters'));
      }
      return { success: false, error: t('modals.upload_url.errors.invalid_parameters') };
    }

    const result = await executeImport(processed.data);

    if (!result) {
      // Don't call `params.setError` here — on real mutation failure the
      // mutation's `onError` has already forwarded the backend's error message
      // (via `variables.context.setError`). Calling again would overwrite that
      // with a generic "Import failed" string. On the "article limit hit" path
      // `handleArticleLimitCheck` already opened the upgrade modal, so no
      // extra surface is needed either.
      return { success: false, error: t('modals.upload_url.errors.import_failed') };
    }

    return { success: true, error: null, data: result };
  };

  const getModalHandlers = () => ({
    [MODAL_NAMES.UPLOAD_URL_MODAL_ID]: {
      type: WebsiteModalTypeEnum.SINGLE,
    },
    [MODAL_NAMES.UPLOAD_MULTIPLE_URLS_MODAL_ID]: {
      type: WebsiteModalTypeEnum.MULTIPLE,
    },
    [MODAL_NAMES.UPLOAD_SITEMAP_MODAL_ID]: {
      type: WebsiteModalTypeEnum.SITEMAP,
    },
    [MODAL_NAMES.UPLOAD_ALL_PAGES_MODAL_ID]: {
      type: WebsiteModalTypeEnum.ALL_PAGES,
    },
    [MODAL_NAMES.UPLOAD_YOUTUBE_MODAL_ID]: {
      type: WebsiteModalTypeEnum.YOUTUBE,
    },
  });

  const getModalIds = (): ModalId[] => [
    MODAL_NAMES.UPLOAD_URL_MODAL_ID,
    MODAL_NAMES.UPLOAD_MULTIPLE_URLS_MODAL_ID,
    MODAL_NAMES.UPLOAD_SITEMAP_MODAL_ID,
    MODAL_NAMES.UPLOAD_ALL_PAGES_MODAL_ID,
  ];

  return {
    importUrl,
    validateUrl,
    getModalHandlers,
    getModalIds,
    isSinglePagePending: singlePageMutation.isPending,
    isMultiplePagesPending: multipageMutation.isPending,
    isSitemapPending: sitemapMutation.isPending,
    isAllPagesPending: siteMapStartMutation.isPending,
    isPending:
      singlePageMutation.isPending ||
      multipageMutation.isPending ||
      sitemapMutation.isPending ||
      allPagesMutation.isPending ||
      siteMapStartMutation.isPending,
    error:
      singlePageMutation.error ||
      multipageMutation.error ||
      sitemapMutation.error ||
      allPagesMutation.error ||
      siteMapStartMutation.error,
  };
};

export const useUploadUrlForm = (
  type: WebsiteModalTypeEnum = WebsiteModalTypeEnum.SINGLE,
  modalIdOverride?: string,
) => {
  const { toggleModal, isOpen } = useModalStore();
  const { importUrl, validateUrl, isPending: hookIsPending } = useImportUrl();
  const [localIsPending, setLocalIsPending] = useState(false);

  const modalId = modalIdOverride ?? UploadUrlModalHelper.getModalIdByType(type);
  const isModalOpen = isOpen(modalId);

  const form = useForm<UploadUrlForm>({
    resolver: zodResolver(uploadUrlSchema),
    defaultValues: {
      url: '',
      htmlSelector: '',
      filterPhrase: '',
      useHtmlSelector: false,
      useFilterPhrase: false,
      tags: [],
      summarize: false,
    },
  });

  const handleClose = useCallback(() => {
    if (!hookIsPending && !localIsPending) {
      toggleModal(modalId);
    }
  }, [toggleModal, modalId, hookIsPending, localIsPending]);

  const handleAdvancedOptionsChange = useCallback(
    (data: {
      useHtmlSelector: boolean;
      htmlSelector: string;
      useFilterPhrase: boolean;
      filterPhrase: string;
    }) => {
      form.setValue('useHtmlSelector', data.useHtmlSelector);
      form.setValue('htmlSelector', data.htmlSelector);
      form.setValue('useFilterPhrase', data.useFilterPhrase);
      form.setValue('filterPhrase', data.filterPhrase);
    },
    [form],
  );

  const handleUrlError = useCallback(
    (error: string | null) => {
      if (error) {
        form.setError('url', {
          type: 'manual',
          message: error,
        });
      } else {
        form.clearErrors('url');
      }
    },
    [form],
  );

  const validateFormUrl = useCallback(
    (value: string) => {
      const error = validateUrl(value, type);
      return error === null || error;
    },
    [validateUrl, type],
  );

  const handleSubmit = useCallback(
    (data: UploadUrlForm, setError?: (error: string) => void) => {
      if (data?.url) {
        setLocalIsPending(true);
        importUrl({
          url: data.url,
          type,
          useHtmlSelector: data.useHtmlSelector,
          htmlSelector: data.htmlSelector,
          useFilterPhrase: data.useFilterPhrase,
          filterPhrase: data.filterPhrase,
          modalId,
          setError,
        }).finally(() => {
          setLocalIsPending(false);
        });
      }
    },
    [importUrl, type, modalId],
  );

  const resetForm = useCallback(() => {
    form.reset();
    setLocalIsPending(false);
  }, [form]);

  useEffect(() => {
    if (!isModalOpen) {
      resetForm();
    }
  }, [isModalOpen, resetForm]);

  const isUrlValid = form.watch('url') && !form.formState.errors.url;
  const isPending = hookIsPending || localIsPending;

  return {
    form,
    handleSubmit,
    handleClose,
    handleUrlError,
    handleAdvancedOptionsChange,
    validateFormUrl,
    isUrlValid,
    isPending,
    isSubmitting: form.formState.isSubmitting,
    modalId,
    isModalOpen,
    resetForm,
  };
};
